import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faMicrophoneSlash } from '@fortawesome/free-solid-svg-icons';
import './VoiceControl.css';

interface VoiceControlProps {
  onCommand: (command: string) => void;
}

declare global {
  interface Window {
    webkitSpeechRecognition: any;
    SpeechRecognition: any;
  }
}

const VoiceControl: React.FC<VoiceControlProps> = ({ onCommand }) => {
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState<any>(null);
  const [transcript, setTranscript] = useState('');

  useEffect(() => {
    // Check if browser supports speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = false;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';

      recognitionInstance.onresult = (event: any) => {
        const current = event.resultIndex;
        const transcript = event.results[current][0].transcript;
        setTranscript(transcript);
        
        // If the result is final, send it as a command
        if (event.results[current].isFinal) {
          onCommand(transcript);
          setTranscript('');
        }
      };

      recognitionInstance.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognitionInstance.onend = () => {
        setIsListening(false);
      };

      setRecognition(recognitionInstance);
    }
  }, [onCommand]);

  const toggleListening = () => {
    if (!recognition) {
      alert('Speech recognition is not supported in your browser. Try Chrome or Edge.');
      return;
    }

    if (isListening) {
      recognition.stop();
      setIsListening(false);
    } else {
      recognition.start();
      setIsListening(true);
    }
  };

  if (!recognition) {
    return null; // Don't show voice control if not supported
  }

  return (
    <div className="voice-control">
      <Button
        variant={isListening ? 'danger' : 'light'}
        className={`voice-button ${isListening ? 'listening' : ''}`}
        onClick={toggleListening}
        title={isListening ? 'Click to stop' : 'Click to speak'}
      >
        <FontAwesomeIcon 
          icon={isListening ? faMicrophoneSlash : faMicrophone} 
          className={isListening ? 'pulse' : ''}
        />
      </Button>
      {transcript && (
        <div className="voice-transcript">
          <span className="listening-indicator">Listening:</span> {transcript}
        </div>
      )}
      {isListening && (
        <div className="voice-hints">
          Try saying: "Show me homes near good schools" or "Find properties under 500k"
        </div>
      )}
    </div>
  );
};

export default VoiceControl;